import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { generateEventId, getCookie, parseMoney, collectAttribution } from '@theme/lead-tracking';

const ITI_VERSION = '23.7.4';
const ITI_CSS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/css/intlTelInput.min.css`;
const ITI_JS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/intlTelInput.min.js`;
const ITI_UTILS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/utils.js`;

const DATALAYER_EVENT = 'generate_lead';
const LEAD_SOURCE = 'product_page_request_quote';
const LEAD_ENDPOINT = '/apps/mira/lead/request-quote';

let itiPromise = null;

function loadIntlTelInput() {
  if (itiPromise) return itiPromise;

  if (!document.querySelector(`link[href="${ITI_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = ITI_CSS;
    document.head.appendChild(link);
  }

  itiPromise = new Promise((resolve, reject) => {
    if (typeof window.intlTelInput === 'function') return resolve();

    const existing = document.querySelector(`script[src="${ITI_JS}"]`);
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      itiPromise = null;
      reject(new Error('intl-tel-input failed to load'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    if (!existing) {
      script.src = ITI_JS;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return itiPromise;
}

export class OrderByPhoneComponent extends Component {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {ReturnType<typeof window.intlTelInput> | null} */
  #iti = null;

  /** @type {Record<string, string>} */
  #variantSnapshot = {};

  /** @type {Record<string, any> | null} */
  #productData = null;

  #previousScrollY = 0;

  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    console.info('[obp] connectedCallback start', {
      hasDialogInside: !!this.querySelector('dialog[data-obp-dialog]'),
      hasFormTemplate: !!this.querySelector('script[data-obp-form-template]'),
    });

    // The component renders inside Shopify's product <form> (via buy-buttons). HTML
    // disallows nested <form> elements — the browser silently strips the inner one
    // during parse. Workaround: ship the contact form as raw text inside a
    // <script type="text/html"> template; hydrate it via JS into the dialog, then
    // portal the dialog out of the product form to body.
    this.#hydrateForm();
    this.#portalDialog();
    this.#productData = this.#readProductData();
    this.#captureVariantSnapshotFromDom();

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    if (this.#dialog) {
      const closeBtn = this.#dialog.querySelector('[data-obp-close]');
      const form = this.#dialog.querySelector('form');
      console.info('[obp] after portal', {
        dialogInBody: this.#dialog.parentNode === document.body,
        foundCloseBtn: !!closeBtn,
        foundForm: !!form,
        formAction: form?.getAttribute('action'),
        formMethod: form?.getAttribute('method'),
      });
      closeBtn?.addEventListener('click', this.closeDialog);
      form?.addEventListener('submit', this.#handleSubmit);
      this.#dialog.addEventListener('click', this.#handleBackdropClick);
    } else {
      console.warn('[obp] no dialog found after portal — submit handler NOT attached');
    }

  }

  #hydrateForm() {
    const template = this.querySelector('script[data-obp-form-template]');
    const mount = this.querySelector('[data-obp-form-mount]');
    if (!template || !mount) {
      console.warn('[obp] hydrateForm — missing template or mount', { template: !!template, mount: !!mount });
      return;
    }

    // Parse the form HTML in an isolated container — no parent <form> to trigger
    // the HTML parser's "no nested forms" stripping.
    const container = document.createElement('div');
    container.innerHTML = template.textContent?.trim() || '';
    const form = container.querySelector('form');
    if (!form) {
      console.warn('[obp] hydrateForm — no <form> in parsed template');
      return;
    }

    mount.replaceWith(form);
    console.info('[obp] form hydrated', { action: form.getAttribute('action') });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.removeEventListener('keydown', this.#handleKeyDown);
    if (this.#dialog?.parentNode === document.body) {
      this.#dialog.remove();
    }
  }

  #portalDialog() {
    const dialog = this.querySelector('dialog[data-obp-dialog]');
    if (!dialog || !(dialog instanceof HTMLDialogElement)) return;
    document.body.appendChild(dialog);
    this.#dialog = dialog;
  }

  // ────────────────────────────────────────────────────────────────
  // Dialog open/close (manual since dialog is portaled out of the
  // component subtree, so DialogComponent's ref-based methods don't apply).
  // ────────────────────────────────────────────────────────────────

  showDialog = async () => {
    if (!this.#dialog || this.#dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      this.#dialog?.showModal();
      document.addEventListener('keydown', this.#handleKeyDown);
    });

    await this.#initPhoneInput();
  };

  closeDialog = async () => {
    if (!this.#dialog || !this.#dialog.open || this.#isClosing) return;
    this.#isClosing = true;

    const dialog = this.#dialog;
    document.removeEventListener('keydown', this.#handleKeyDown);

    // Re-trigger the closing animation
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } catch {
      /* ignore */
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#isClosing = false;
  };

  #handleBackdropClick = (event) => {
    if (!this.#dialog) return;
    if (isClickedOutside(event, this.#dialog.firstElementChild || this.#dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  // ────────────────────────────────────────────────────────────────

  async #initPhoneInput() {
    if (this.#iti) return;
    const phoneInput = /** @type {HTMLInputElement | null} */ (
      this.#dialog?.querySelector('[ref="phoneInput"]') || null
    );
    if (!phoneInput) return;

    try {
      await loadIntlTelInput();
      if (typeof window.intlTelInput !== 'function') throw new Error('intlTelInput not on window');
      this.#iti = window.intlTelInput(phoneInput, {
        initialCountry: 'za',
        preferredCountries: ['za', 'gb', 'us'],
        separateDialCode: true,
        autoPlaceholder: 'off',
        utilsScript: ITI_UTILS,
      });
    } catch (e) {
      console.warn('[order-by-phone] intl-tel-input failed to load; using plain tel input', e);
    }
  }

  #readProductData() {
    const script = this.querySelector('script[data-obp-product]');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[order-by-phone] product JSON parse failed', e);
      return null;
    }
  }

  #handleVariantUpdate = (event) => {
    const variant = event?.detail?.resource;
    if (!variant) return;
    if (event.detail?.data?.productId && event.detail.data.productId !== this.#productId()) return;

    this.#updateVariantDisplay(variant);

    if (this.#productData) {
      this.#productData.variant_id = variant.id;
      this.#productData.variant_title = variant.title;
      this.#productData.variant_sku = variant.sku || this.#productData.variant_sku;
      if (typeof variant.price === 'number') {
        this.#productData.price = variant.price / 100;
      }
    }
  };

  #productId() {
    return this.closest('[data-product-id]')?.getAttribute('data-product-id') || null;
  }

  #captureVariantSnapshotFromDom() {
    const optionsEl = this.#dialog?.querySelector('[ref="productOptions"]');
    if (!optionsEl) return;
    /** @type {Record<string,string>} */
    const snapshot = {};
    optionsEl.querySelectorAll('.order-by-phone-dialog__option').forEach((opt) => {
      const name = opt.querySelector('dt')?.textContent?.trim();
      const value = opt.querySelector('dd')?.textContent?.trim();
      if (name) snapshot[name] = value || '';
    });
    this.#variantSnapshot = snapshot;
  }

  #updateVariantDisplay(variant) {
    if (!variant || !this.#dialog) return;

    const optionsEl = this.#dialog.querySelector('[ref="productOptions"]');
    const priceEl = this.#dialog.querySelector('[ref="priceDisplay"]');

    if (optionsEl && Array.isArray(variant.options)) {
      const optionRows = optionsEl.querySelectorAll('.order-by-phone-dialog__option');
      const snapshot = {};
      optionRows.forEach((row, idx) => {
        const dd = row.querySelector('dd');
        const dt = row.querySelector('dt');
        if (dd && variant.options[idx] !== undefined) {
          dd.textContent = variant.options[idx];
        }
        if (dt) snapshot[dt.textContent?.trim() || ''] = variant.options[idx] || '';
      });
      this.#variantSnapshot = snapshot;
    }

    if (typeof variant.price === 'number' && priceEl) {
      priceEl.textContent = this.#formatMoney(variant.price);
    }
  }

  #formatMoney(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, '{{ amount }}');
      }
    } catch {
      /* fall through */
    }
    return `R ${(cents / 100).toFixed(2)}`;
  }

  #refIn(name) {
    return this.#dialog?.querySelector(`[ref="${name}"]`) || null;
  }

  /**
   * Submit the quote request to the Mira app proxy.
   *
   * The app emails the lead (Mailgun) and fires the conversion server-side, so
   * the customer never leaves the page: on success the form is replaced with
   * the success message in place. That's what keeps the conversion to exactly
   * one event — the old native contact-form submit redirected, which meant a
   * push before unload and another on the success render.
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.#dialog) {
      console.warn('[obp] submit aborted — no dialog reference');
      return;
    }

    const form = event?.target instanceof HTMLFormElement ? event.target : this.#dialog.querySelector('form');
    if (!form) {
      console.warn('[obp] submit aborted — no form element');
      return;
    }

    // Native constraint validation (required name + email) runs first.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const first = /** @type {HTMLInputElement | null} */ (this.#refIn('firstName'))?.value?.trim() || '';
    const last = /** @type {HTMLInputElement | null} */ (this.#refIn('lastName'))?.value?.trim() || '';
    const email = /** @type {HTMLInputElement | null} */ (this.#refIn('emailInput'))?.value?.trim() || '';
    const message = /** @type {HTMLTextAreaElement | null} */ (this.#refIn('messageInput'))?.value?.trim() || '';
    const honeypot = /** @type {HTMLInputElement | null} */ (this.#refIn('honeypot'))?.value?.trim() || '';
    const phoneInput = /** @type {HTMLInputElement | null} */ (this.#refIn('phoneInput'));

    let phoneValue = '';
    let phoneCountry = '';
    if (this.#iti) {
      if (typeof this.#iti.getNumber === 'function') phoneValue = this.#iti.getNumber() || '';
      if (typeof this.#iti.getSelectedCountryData === 'function') {
        phoneCountry = (this.#iti.getSelectedCountryData()?.iso2 || '').toLowerCase();
      }
    }
    if (!phoneValue) phoneValue = phoneInput?.value?.trim() || '';

    const details = this.#collectSelectionDetails();
    const product = this.#productData;
    const eventId = generateEventId();
    // The configurator's displayed price follows the selected fabric and size;
    // product.price is the render-time variant price and can be stale.
    const quotedValue = parseMoney(details.priceDisplay);

    // Absolute product URL for the email (Liquid's product.url is relative).
    let productUrl = product?.url || '';
    if (productUrl.startsWith('/')) productUrl = window.location.origin + productUrl;

    const payload = {
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      lead_source: LEAD_SOURCE,
      form_id: this.dataset.formId || '',
      price_display: details.priceDisplay,
      user: {
        first_name: first,
        last_name: last,
        email,
        phone: phoneValue,
        country: phoneCountry || 'za',
      },
      product: product || null,
      variant_options: this.#variantSnapshot,
      message_provided: Boolean(message),
      page: {
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer || '',
      },
      meta: {
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
        user_agent: navigator.userAgent,
      },
    };

    // Lead payload for the app proxy. No recipient address is sent — the app
    // resolves the destination inboxes from its own configuration.
    const requestBody = {
      company: honeypot,
      first_name: first,
      last_name: last,
      email,
      phone: phoneValue,
      country: phoneCountry || 'za',
      message,
      summary: this.#buildProductSummary(),
      product: product
        ? {
            title: product.title,
            handle: product.handle,
            url: productUrl,
            variant_title: product.variant_title,
            price: quotedValue ?? product.price,
            currency: product.currency,
          }
        : null,
      image_url: details.imageUrl,
      material_name: details.materialName,
      material_swatch_url: details.materialSwatchUrl,
      dimensions: details.dimensions,
      quantity: details.quantity,
      price_display: details.priceDisplay,
      page_url: window.location.href,
      event_id: eventId,
      attribution: collectAttribution(),
    };

    const endpoint = this.dataset.leadEndpoint || LEAD_ENDPOINT;

    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('.order-by-phone-form__submit')
    );
    const originalLabel = submitBtn?.textContent || '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.loading = 'true';
      submitBtn.textContent = 'Sending…';
    }
    this.#clearStatus();

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestBody),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON response */
      }
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      this.#renderSuccess();
      // Pushed only once the lead is confirmed sent, so a failed submission is
      // never reported as a conversion.
      this.#pushDataLayer(payload, 'success_render');
    } catch (err) {
      console.warn('[obp] lead submit failed', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.dataset.loading = '';
        submitBtn.textContent = originalLabel;
      }
      this.#showError(
        'Sorry — something went wrong sending your request. Please try again, or email us at hello@muze.co.za.'
      );
    }
  };

  #renderSuccess() {
    const form = this.#dialog?.querySelector('form');
    if (!form) return;
    const message = this.dataset.successMessage || 'Thanks — your request has been sent.';
    const success = document.createElement('div');
    success.className = 'order-by-phone-form__success';
    success.tabIndex = -1;
    success.textContent = message;
    form.replaceChildren(success);
    requestAnimationFrame(() => success.focus());
  }

  #showError(message) {
    const status = this.#refIn('formStatus');
    if (!status) return;
    status.className = 'order-by-phone-form__status order-by-phone-form__error';
    status.textContent = message;
    status.hidden = false;
  }

  #clearStatus() {
    const status = this.#refIn('formStatus');
    if (!status) return;
    status.className = 'order-by-phone-form__status';
    status.textContent = '';
    status.hidden = true;
  }

  /**
   * What the shopper actually configured, read from the Mira configurator on
   * the page rather than the variant — the fabric, size and price shown there
   * are what they're asking to be quoted on.
   */
  #collectSelectionDetails() {
    const imgEl = this.#dialog?.querySelector('.order-by-phone-dialog__product-image img');
    const imageUrl = imgEl?.currentSrc || imgEl?.getAttribute('src') || '';

    const selectedCard = document.querySelector('.mira-fabric-card--selected');
    const materialName =
      selectedCard?.getAttribute('aria-label')?.trim() ||
      this.#selectedFabric() ||
      this.#productData?.variant_title ||
      '';

    const swatchEl = selectedCard?.querySelector('.mira-fabric-card__thumb img');
    const materialSwatchUrl = swatchEl?.currentSrc || swatchEl?.getAttribute('src') || '';

    // Each configurator step keeps a one-line summary of its choice (size,
    // fabric, …). Collecting them generically means new steps appear in the
    // email without this needing to know what they are.
    const stepSummaries = Array.from(document.querySelectorAll('.mira-step'))
      .map((step) => step.querySelector('.mira-step__summary')?.textContent?.trim() || '')
      .filter(Boolean);

    return {
      imageUrl,
      materialName,
      materialSwatchUrl,
      dimensions: stepSummaries.join('  ·  '),
      quantity: this.#refIn('quantityDisplay')?.textContent?.trim() || '1',
      priceDisplay: this.#quotedPriceText(),
    };
  }

  /** The quoted price — the configurator's, which reflects the chosen fabric
   *  and size, falling back to the dialog's variant price. */
  #quotedPriceText() {
    const configured = document.querySelector('.mira-price-current')?.textContent?.trim();
    return configured || this.#refIn('priceDisplay')?.textContent?.trim() || '';
  }

  /** Fabric from the dialog's option rows, whatever the option is labelled. */
  #selectedFabric() {
    const snapshot = this.#variantSnapshot || {};
    for (const [name, value] of Object.entries(snapshot)) {
      if (/fabric|colour|color|material/i.test(name) && value) return value;
    }
    return Object.values(snapshot)[0] || '';
  }

  #pushDataLayer(payload, phase) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];

    const product = payload.product || {};
    const value = Number(product.price) || 0;
    const currency = product.currency || 'ZAR';
    const variantId = product.variant_id ? String(product.variant_id) : '';

    window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({
      event: DATALAYER_EVENT,
      event_id: payload.event_id,
      event_time: payload.event_time,
      event_phase: phase,
      lead_source: payload.lead_source,
      form_id: payload.form_id,

      content_name: product.title || '',
      content_ids: variantId ? [variantId] : [],
      content_type: 'product',
      content_category: product.product_type || '',
      currency,
      value,
      num_items: 1,

      user_data: {
        email_address: payload.user?.email || '',
        phone_number: payload.user?.phone || '',
        address: {
          first_name: payload.user?.first_name || '',
          last_name: payload.user?.last_name || '',
          country: payload.user?.country || '',
        },
      },

      fbp: payload.meta?.fbp || undefined,
      fbc: payload.meta?.fbc || undefined,

      product: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor,
        product_type: product.product_type,
        url: product.url,
        variant_id: product.variant_id,
        variant_title: product.variant_title,
        variant_sku: product.variant_sku,
        price: value,
        compare_at_price: product.compare_at_price || null,
        currency,
        variant_options: payload.variant_options || {},
        collection_handles: typeof product.collection_handles === 'string'
          ? product.collection_handles.split(',').filter(Boolean)
          : [],
      },

      ecommerce: {
        currency,
        value,
        items: [
          {
            item_id: variantId || String(product.id || ''),
            item_name: product.title || '',
            item_variant: product.variant_title || '',
            item_brand: product.vendor || '',
            item_category: product.product_type || '',
            price: value,
            quantity: 1,
          },
        ],
      },

      page: payload.page || {},
    });
  }

  #buildProductSummary() {
    const titleEl = this.#dialog?.querySelector('.order-by-phone-dialog__product-title');
    const title = titleEl?.textContent?.trim() || '';
    const qty = this.#refIn('quantityDisplay')?.textContent?.trim() || '1';
    const price = this.#refIn('priceDisplay')?.textContent?.trim() || '';

    if (!title) return '';

    const lines = [`Product: ${title}`];
    const optionLines = Object.entries(this.#variantSnapshot)
      .filter(([, value]) => value)
      .map(([name, value]) => `  ${name}: ${value}`);
    if (optionLines.length) {
      lines.push('Options:');
      lines.push(...optionLines);
    }
    lines.push(`Quantity: ${qty}`);
    if (price) lines.push(`Price: ${price}`);
    return lines.join('\n');
  }
}

if (!customElements.get('order-by-phone-component')) {
  customElements.define('order-by-phone-component', OrderByPhoneComponent);
}
